'use client';

import type { HTMLAttributes, PropsWithChildren, ReactNode } from 'react';
import React, { useRef } from 'react';
import clsx from 'clsx';
import { Portal } from 'components/Portal';
import {
  useFloatingPosition,
  type UseFloatingPositionProps,
} from 'hooks/useFloatingPosition/useFloatingPosition';
import type { ReferenceElement } from '@floating-ui/dom';
import {
  mergeRefs,
  useEnterAnimation,
  useExitAnimation,
} from '@react-aria/utils';
import { theme } from '@frameio/vapor-theme';
import { Arrow } from './components/Arrow';
import styles from './Tooltip.module.css';

/**
 * The shared tooltip positioning props that extend the base positioning props
 * with additional component-specific positioning options.
 */
export type TooltipPositioningProps = {
  /** The target element or virtual element */
  target?: ReferenceElement;
  /**
   * Offset along the main axis and cross axis
   * @example [0, 10] - 0px along main axis, 10px along cross axis
   */
  offset?: readonly [number, number];
  /** The z-index of the tooltip */
  zIndex?: number;

  // Omit the props that are internal to the positioning logic
  // but are consumed differently at the component level
} & Pick<
  UseFloatingPositionProps,
  | 'placement'
  | 'boundary'
  | 'autoHide'
  | 'fallbackPlacements'
  | 'overflowPadding'
>;

export type TooltipContentProps = {
  /** Content props */
  /** The primary label of the Tooltip. */
  title?: ReactNode | string;
  /** The secondary label of the Tooltip. */
  details?: ReactNode;
  /** An element to render before the text content. */
  adornmentBefore?: ReactNode;
  /** An element to render after the text content. */
  adornmentAfter?: ReactNode;
  /** max-width for the Tooltip container. */
  maxWidth?: string;
  /** The children of the Tooltip. */
  children?: ReactNode;

  /** Animation props */
  /** Whether or not the enter animation should play when the Tooltip mounts. */
  animateOnMount?: boolean;
  /** Whether or not the exit animation should play when the Tooltip unmounts. */
  animateOnUnmount?: boolean;

  /** State props */
  /** Whether the tooltip is showing */
  isShowing?: boolean;

  /** Rendering props */
  /** Whether to use the portal */
  shouldUsePortal?: boolean;
  /** Whether to use the positioner */
  shouldUsePositioner?: boolean;

  /** Accessibility props */
  /** ID of the tooltip */
  id?: string;
  /** The role of the tooltip */
  role?: string;

  /** @deprecated - No longer needed */
  observeContentSize?: boolean;
  /** @deprecated - No longer needed */
  observeTargetSize?: boolean;
} & TooltipPositioningProps &
  Omit<HTMLAttributes<HTMLDivElement>, 'title'>;

const defaultOffset = [0, 8] as const;

/**
 * A "dumb" version of the Tooltip which renders a Tooltip without
 * the behavior controls provided by `Tooltip`.
 */

export const TooltipContent = React.forwardRef<any, TooltipContentProps>(
  function TooltipContent(
    {
      // Content props
      adornmentBefore,
      adornmentAfter,
      title,
      details,
      children,

      // @deprecated and now unused
      observeContentSize: __observeContentSize,
      observeTargetSize: __observeTargetSize,

      // State props
      isShowing,
      shouldUsePortal = false,
      animateOnMount = true,
      animateOnUnmount = true,
      offset = defaultOffset,
      ...other
    },
    ref
  ) {
    const localRef = useRef(null);

    const isExiting =
      useExitAnimation(localRef, Boolean(isShowing)) && animateOnUnmount;

    if (!isShowing && !isExiting) {
      return null;
    }

    const offsetMainAxis = offset[1];
    const offsetCrossAxis = offset[0];

    return (
      <Portal disabled={!shouldUsePortal}>
        <PositionedTooltip
          ref={mergeRefs(localRef, ref)}
          animateOnMount={animateOnMount}
          isExiting={isExiting}
          offsetCrossAxis={offsetCrossAxis}
          offsetMainAxis={offsetMainAxis}
          {...other}
        >
          {children || (
            <div className={styles.content}>
              {adornmentBefore && (
                <div className={styles.adornment}>{adornmentBefore}</div>
              )}
              <div className={styles.textContent}>
                {typeof title === 'string' ? (
                  <TooltipTitle>{title}</TooltipTitle>
                ) : (
                  title
                )}
                {details && <TooltipDetails>{details}</TooltipDetails>}
              </div>
              {adornmentAfter && (
                <div className={styles.adornment}>{adornmentAfter}</div>
              )}
            </div>
          )}
        </PositionedTooltip>
      </Portal>
    );
  }
);

/**
 * Renders the Tooltip content with arrow, and positions it
 * relative to the target element.
 */

type PositionedTooltipProps = PropsWithChildren<
  {
    /** Whether the tooltip is exiting */
    isExiting?: boolean;
    /** Whether to use the positioner */
    shouldUsePositioner?: boolean;
    /** Whether to animate on mount */
    animateOnMount?: boolean;
    /** Offset along the main axis */
    offsetMainAxis?: number;
    /** Offset along the cross axis */
    offsetCrossAxis?: number;
    /** max-width for the Tooltip container. */
    maxWidth?: string;
    /** Optional className */
    className?: string;
    /** Optional style */
    style?: React.CSSProperties;
  } & TooltipPositioningProps
>;

const PositionedTooltip = React.forwardRef<
  HTMLDivElement,
  PositionedTooltipProps
>(
  (
    {
      placement = 'top',
      offsetMainAxis,
      isExiting,
      offsetCrossAxis,
      shouldUsePositioner = false,
      target,
      animateOnMount,
      children,
      maxWidth = '296px',
      boundary,
      autoHide = true,
      fallbackPlacements,
      zIndex = theme.zIndices.tooltip,
      className,
      style,
      ...other
    },
    ref
  ) => {
    const tooltipRef = useRef<HTMLDivElement>(null);
    const arrowRef = useRef<HTMLDivElement>(null);

    const { placement: floatingUIPlacement, referenceHidden } =
      useFloatingPosition(target, tooltipRef, {
        enabled: shouldUsePositioner,
        placement,
        arrowRef,
        offsetMainAxis,
        offsetCrossAxis,
        boundary,
        autoHide,
        fallbackPlacements,
      });

    const resolvedPlacement = shouldUsePositioner
      ? floatingUIPlacement
      : placement;

    const isEntering =
      useEnterAnimation(tooltipRef, Boolean(resolvedPlacement)) &&
      animateOnMount;

    // This state occurs when `isShowing` is true but `target` is not available,
    // which can occur when a Tooltip is controlled and has `isShowing`
    // set to true on mount. Let's just unmount the tooltip for now to match
    // existing behavior.
    if (!target && shouldUsePositioner) {
      return null;
    }

    const mergedStyle = {
      ...style,
      zIndex,
      display: referenceHidden ? 'none' : undefined,
      '--vapor-tooltip-max-width': maxWidth,
    } as React.CSSProperties;

    return (
      <div
        ref={mergeRefs(tooltipRef, ref)}
        className={clsx(styles.tooltipContainer, className)}
        data-entering={isEntering || undefined}
        data-exiting={isExiting || undefined}
        data-position={resolvedPlacement || undefined}
        data-testid="tooltip-container"
        style={mergedStyle}
        {...other}
      >
        <Arrow ref={arrowRef} position={resolvedPlacement || placement} />
        {children}
      </div>
    );
  }
);

/**
 * TooltipTitle - A styled component for tooltip titles
 */
export function TooltipTitle({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  ref?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      ref={ref}
      className={clsx(styles.tooltipTitle, className)}
      {...props}
    />
  );
}

/**
 * TooltipDetails - A styled component for tooltip details/secondary text
 */
export function TooltipDetails({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  ref?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      ref={ref}
      className={clsx(styles.tooltipDetails, className)}
      {...props}
    />
  );
}
